import * as GQL from "src/types/graphql";
import {
  GetMessageTemplateTypes,
  message_template_insert_input,
  message_template_type_enum,
  message_template_updates,
  UpdateMessageTemplatesVariables,
} from "src/types/graphql";
import {
  defaultTemplateRankDisabled,
  defaultTemplateRankEnabled,
} from "../messageTemplateBody";
import { ListMessageTemplatesFormType, MessageTemplateType } from "./schemas";

export function formatMessageTemplates(
  data: GetMessageTemplateTypes,
  formTemplateId: uuid,
  schoolList?: GQL.GetSchoolsForEnrollmentPeriod_school[]
): MessageTemplateType[] {
  const { message_template_type, message_template } = data;

  return message_template_type.flatMap(({ value }): MessageTemplateType[] => {
    const messageTemplates = message_template.filter(
      (template) => template.type === value
    );

    // add null to the list of school ids to represent the standard template
    const schoolIds = schoolList
      ? [null, ...schoolList.map((school) => school.id)]
      : [null];

    return schoolIds.map((schoolId) => {
      const messageTemplate = messageTemplates.find(
        (template) => template.school_id === schoolId
      );
      return {
        id: messageTemplate?.id,
        formTemplateId,
        type: value as message_template_type_enum,
        enabled: messageTemplate?.enabled ?? false,
        schoolId: schoolId ?? undefined,
      };
    });
  });
}

export function formatInitialValues(
  messageTemplates: MessageTemplateType[]
): ListMessageTemplatesFormType {
  return messageTemplates.reduce((accumulator, template) => {
    const accTemplate = accumulator[template.type] ?? {
      standard: null,
      schoolSpecific: {},
    };

    if (template.schoolId) {
      accTemplate.schoolSpecific[template.schoolId] = template;
    } else {
      accTemplate.standard = template;
    }

    accumulator[template.type] = accTemplate;

    return accumulator;
  }, {} as ListMessageTemplatesFormType);
}

export function transformFormValuesToMutationVariables(
  formValues: ListMessageTemplatesFormType,
  rankingEnabled: boolean
): UpdateMessageTemplatesVariables {
  // Flatten all templates (both standard and school-specific) into a single array
  const messageTemplates = Object.values(formValues)
    .flatMap((templatesByType) => [
      templatesByType.standard,
      ...Object.values(templatesByType.schoolSpecific),
    ])
    .filter((template): template is MessageTemplateType => template !== null);

  // inserts message template objects that do not have an id and enabled is set to true
  const messageTemplatesToInsert = messageTemplates.filter(
    (template) => template.id === undefined && template.enabled === true
  );

  // update message templates that have an id
  const messageTemplatesToUpdate = messageTemplates.filter(
    (template) => template.id !== undefined
  );

  return {
    messageTemplateInserts: insertMessageTemplatePayload(
      messageTemplatesToInsert,
      rankingEnabled
    ),
    messageTemplateUpdates: updateMessageTemplatePayload(
      messageTemplatesToUpdate
    ),
  };
}

function insertMessageTemplatePayload(
  messageTemplates: MessageTemplateType[],
  rankingEnabled: boolean
): message_template_insert_input[] {
  return messageTemplates.map((template) => {
    const { formTemplateId, type, enabled, schoolId } = template;

    const defaultTemplate = rankingEnabled
      ? defaultTemplateRankEnabled
      : defaultTemplateRankDisabled;

    const { emailSubject, emailText, emailHtml, sms } = defaultTemplate[type];

    return {
      form_template_id: formTemplateId,
      school_id: schoolId,
      type,
      email_subject: emailSubject,
      email_plain_text: emailText,
      email_markup: emailHtml,
      sms_body: sms,
      enabled,
    };
  });
}

function updateMessageTemplatePayload(
  messageTemplates: MessageTemplateType[]
): message_template_updates[] {
  return messageTemplates.map((template) => {
    const { id, formTemplateId, type, schoolId, enabled } = template;

    return {
      where: {
        id: { _eq: id },
        form_template_id: { _eq: formTemplateId },
        type: { _eq: type },
        school_id: schoolId ? { _eq: schoolId } : { _is_null: true },
        enabled: { _neq: enabled }, // prevent no-op mutations
      },
      _set: {
        enabled: enabled,
      },
    };
  });
}
